import React, { memo, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Alert } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { Button } from 'atoms';
import {
  ARTIFACT_URL_FIELD,
  CareerCompletionKind,
  useCompleteCareerLesson,
} from 'core/hooks/CareerCenter';

export interface ICareerLessonComplete {
  lessonId: string;
  completionKind: CareerCompletionKind;
  /** Helper text under the link input, authored per lesson in the ERP. */
  artifactHint: string;
  isCompleted: boolean;
  completedAt: string | null;
  /** Called after a successful completion so the page can refresh the lesson. */
  onCompleted: () => void;
}

interface IArtifactFormValues {
  artifactUrl: string;
}

/**
 * The «Пройдено» control at the foot of a career lesson.
 *
 * Two shapes driven by `completionKind`:
 *  - 'acknowledge' — a bare confirm button.
 *  - 'artifact'    — a REQUIRED link input first; the server rejects a missing
 *                    link with a customError on field `artifactUrl`, and Yup
 *                    mirrors that rule client-side so the student gets the
 *                    message without a round-trip. The server stays the
 *                    authority — a server-side field error is surfaced verbatim.
 *
 * An already-completed lesson renders a static confirmation instead of the
 * button: re-submitting is harmless (the backend's unique progress row is the
 * guard) but offering it would imply the click does something.
 */
export const CareerLessonComplete = memo(
  ({
    lessonId,
    completionKind,
    artifactHint,
    isCompleted,
    completedAt,
    onCompleted,
  }: ICareerLessonComplete) => {
    const { t } = useTranslation();
    const { completeLesson, loading } = useCompleteCareerLesson();

    const needsArtifact = completionKind === 'artifact';

    const formik = useFormik<IArtifactFormValues>({
      initialValues: { artifactUrl: '' },
      validationSchema: Yup.object({
        artifactUrl: needsArtifact
          ? Yup.string()
              .trim()
              .url(`${t('careerCenter.lesson.artifact.invalid')}`)
              .required(`${t('careerCenter.lesson.artifact.required')}`)
          : Yup.string(),
      }),
      onSubmit: async (values, helpers) => {
        try {
          const result = await completeLesson(
            lessonId,
            needsArtifact ? values.artifactUrl : undefined,
          );

          if (result.success) {
            onCompleted();
            return;
          }

          // A field-scoped server error lands on the input; anything else
          // (including an unrecognized field) becomes a form-level message.
          const message = result.errorMessage || `${t('careerCenter.lesson.completeError')}`;

          if (result.errorField === ARTIFACT_URL_FIELD) {
            helpers.setFieldError(ARTIFACT_URL_FIELD, message);
            return;
          }

          helpers.setStatus(message);
        } catch {
          // Network/transport failure — the mutation never reached the server.
          helpers.setStatus(`${t('careerCenter.lesson.completeError')}`);
        }
      },
    });

    const onSubmit = useCallback(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        formik.handleSubmit();
      },
      [formik],
    );

    if (isCompleted) {
      return (
        <CompletedBox>
          <CompletedMark aria-hidden>✓</CompletedMark>
          <div>
            <CompletedTitle>{t('careerCenter.lesson.completedTitle')}</CompletedTitle>
            {completedAt ? (
              <CompletedDate>
                {t('careerCenter.lesson.completedAt', {
                  date: new Date(completedAt).toLocaleDateString(),
                })}
              </CompletedDate>
            ) : null}
          </div>
        </CompletedBox>
      );
    }

    return (
      <Form onSubmit={onSubmit} noValidate>
        {needsArtifact ? (
          <Field>
            <FieldLabel htmlFor={ARTIFACT_URL_FIELD}>
              {t('careerCenter.lesson.artifact.label')}
            </FieldLabel>
            <FieldInput
              id={ARTIFACT_URL_FIELD}
              name={ARTIFACT_URL_FIELD}
              type="url"
              inputMode="url"
              placeholder={t('careerCenter.lesson.artifact.placeholder')}
              value={formik.values.artifactUrl}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              hasError={Boolean(formik.touched.artifactUrl && formik.errors.artifactUrl)}
              disabled={loading}
              aria-describedby={`${ARTIFACT_URL_FIELD}-hint`}
            />
            <FieldHint id={`${ARTIFACT_URL_FIELD}-hint`}>
              {artifactHint || t('careerCenter.lesson.artifact.defaultHint')}
            </FieldHint>
            {formik.touched.artifactUrl && formik.errors.artifactUrl ? (
              <FieldError>{formik.errors.artifactUrl}</FieldError>
            ) : null}
          </Field>
        ) : null}

        {formik.status ? <Alert type="error" showIcon message={formik.status} /> : null}

        <SubmitButton type="submit" isLoading={loading} disabled={loading}>
          {t('careerCenter.lesson.complete')}
        </SubmitButton>
      </Form>
    );
  },
);

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${Colors.system_gray};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 560px;
`;

const FieldLabel = styled.label`
  margin-bottom: 8px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
`;

const FieldInput = styled.input<{ hasError?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ hasError }) => (hasError ? Colors.signal_red : Colors.system_gray)};
  border-radius: 8px;
  background-color: ${Colors.white};
  color: ${Colors.black};
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? Colors.signal_red : Colors.main)};
  }

  &:disabled {
    background-color: ${Colors.bg_gray};
    cursor: not-allowed;
  }
`;

const FieldHint = styled.span`
  margin-top: 8px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const FieldError = styled.span`
  margin-top: 8px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.signal_red};
`;

const SubmitButton = styled(Button)`
  width: auto;
  align-self: flex-start;

  ${mobile} {
    width: 100%;
  }
`;

const CompletedBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 32px;
  padding: 20px 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: ${Colors.bg_gray};
`;

const CompletedMark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${Colors.marker_green};
  color: ${Colors.white};
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const CompletedTitle = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const CompletedDate = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
